import type { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  OnlinePaymentProvider,
  OrderStatus,
  SellerTransactionStatus,
} from "@/lib/api/types";
import {
  ORDER_STATUS_VALUES,
  getOrderStatusLabel,
} from "@/modules/orders/components/order-status";

/** Every status the transactions API can return, in display order. */
export const TRANSACTION_STATUS_VALUES: SellerTransactionStatus[] = [
  "initiated",
  "pending",
  "paid",
  "failed",
  "refunded",
  "cancelled",
  "expired",
];

/** Gateways this app knows how to brand. A transaction from any other gateway
 * still renders — `getProviderLabel` falls back to the raw slug. */
export const TRANSACTION_PROVIDER_VALUES: OnlinePaymentProvider[] = [
  "paymob",
  "paytabs",
  "fawry",
  "kashier",
  "stripe",
];

/** Reuses the per-order payment labels rather than duplicating the same seven
 * words in a second namespace — one source of truth for "paid" in Arabic. */
const STATUS_LABEL_KEY: Record<SellerTransactionStatus, string> = {
  initiated: keys.orders.onlinePayStatusInitiated,
  pending: keys.orders.onlinePayStatusPending,
  paid: keys.orders.onlinePayStatusPaid,
  failed: keys.orders.onlinePayStatusFailed,
  refunded: keys.orders.onlinePayStatusRefunded,
  cancelled: keys.orders.onlinePayStatusCancelled,
  expired: keys.orders.onlinePayStatusExpired,
};

const PROVIDER_LABEL_KEY: Record<OnlinePaymentProvider, string> = {
  paymob: keys.paymentGateway.brandPaymob,
  paytabs: keys.paymentGateway.brandPaytabs,
  kashier: keys.paymentGateway.brandKashier,
  fawry: keys.paymentGateway.brandFawry,
  stripe: keys.paymentGateway.brandStripe,
};

/** Semantic tone per status — same mapping as the order drawer's payment panel.
 * `fg` colours the label + dot; `bg` tints the pill. Never colour alone: the
 * pill always carries its text label too. */
export type TransactionStatusTone = { fg: string; bg: string };

const STATUS_TONE: Record<SellerTransactionStatus, TransactionStatusTone> = {
  paid: {
    fg: "var(--success-500)",
    bg: "color-mix(in oklab, var(--success-500) 12%, transparent)",
  },
  refunded: {
    fg: "var(--text-secondary)",
    bg: "var(--surface-alt)",
  },
  cancelled: {
    fg: "var(--text-muted)",
    bg: "var(--surface-alt)",
  },
  failed: {
    fg: "var(--danger-500)",
    bg: "color-mix(in oklab, var(--danger-500) 12%, transparent)",
  },
  expired: {
    fg: "var(--danger-500)",
    bg: "color-mix(in oklab, var(--danger-500) 12%, transparent)",
  },
  pending: {
    fg: "var(--warning-500)",
    bg: "color-mix(in oklab, var(--warning-500) 14%, transparent)",
  },
  initiated: {
    fg: "var(--warning-500)",
    bg: "color-mix(in oklab, var(--warning-500) 14%, transparent)",
  },
};

export function getTransactionStatusTone(
  status: SellerTransactionStatus,
): TransactionStatusTone {
  return STATUS_TONE[status];
}

export function getTransactionStatusLabel(
  t: ReturnType<typeof useTranslations>,
  status: SellerTransactionStatus,
): string {
  return t(STATUS_LABEL_KEY[status]);
}

function isKnownProvider(value: string): value is OnlinePaymentProvider {
  return (TRANSACTION_PROVIDER_VALUES as string[]).includes(value);
}

/** Branded name for a known gateway; the raw slug for anything newer than this
 * build, so an unknown provider degrades to readable text instead of blank. */
export function getProviderLabel(
  t: ReturnType<typeof useTranslations>,
  provider: string,
): string {
  return isKnownProvider(provider) ? t(PROVIDER_LABEL_KEY[provider]) : provider;
}

function isOrderStatus(value: string): value is OrderStatus {
  return (ORDER_STATUS_VALUES as string[]).includes(value);
}

/** The linked order's status comes over the wire as a bare string. Label it when
 * it is a status this app knows, otherwise show the raw value. */
export function getLinkedOrderStatusLabel(
  t: ReturnType<typeof useTranslations>,
  status: string,
): string {
  return isOrderStatus(status) ? getOrderStatusLabel(t, status) : status;
}

/** Day + month + year, matching the orders surfaces' date style. */
export function formatTransactionDate(value: string, locale: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

/** Date + time, for the detail panel and the event timeline. */
export function formatTransactionDateTime(
  value: string,
  locale: string,
): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
}

/** A hosted-checkout link is only actionable while the buyer can still pay. */
export function canShowCheckoutUrl(
  status: SellerTransactionStatus,
  checkoutUrl: string | null,
): checkoutUrl is string {
  return (
    checkoutUrl !== null &&
    checkoutUrl.length > 0 &&
    (status === "initiated" || status === "pending")
  );
}
